import React, { useEffect, useRef, useState } from "react";
import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import { Icons } from "atoms/constants/icons";
import { ButtonStyles } from "atoms/constants/button-styles";
import Paragraph from "atoms/typography/paragraph";
import { ParagraphSizes } from "atoms/constants/paragraph-sizes";
import ChatModalResults from "organisms/modals/chat-modal/chat-results";
import ChatPrompt from "organisms/modals/chat-modal/chat-prompt";
import { CurrentConversationProvider } from "utilities/atoms/conversations/current-conversation/current-conversation-provider";
import { BookviewConversationAtom } from "utilities/atoms/conversations/current-conversation/conversation-atoms";
import { useHistory, useLocation } from "react-router";
import { useConversationActions } from "utilities/atoms/conversations/current-conversation/use-conversation-actions.hook";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import ChatOfflineTooltipContent from "organisms/modals/chat-modal/chat-offline-tooltip-content";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import BookviewTabPanelFooter from "./bookview-tab-panel-footer";

const BASE_CLASS_NAME = "c-bookview-chat-panel";
const CONVERSATION_ID = "conversationId";

const BookviewChatPanelContent: React.FC = () => {
    const { clearConversation, loadConversation } = useConversationActions();
    const { isOnline } = useNetworkInformation();
    const { search, pathname, hash } = useLocation();
    const history = useHistory();
    const [prompt, setPrompt] = useState("");
    const [newChatStarted, setNewChatStarted] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const offlineClassName = !isOnline ? "-offline" : "";
    const { inMobileView } = useBreakpointRanges();

    const handleClearChat = () => {
        if (!isOnline) {
            return;
        }

        clearConversation();
        setPrompt("");
        setNewChatStarted(true);
    };

    useEffect(() => {
        const query = new URLSearchParams(search);
        const conversationId = query.get(CONVERSATION_ID);
        if (conversationId) {
            loadConversation(conversationId);

            query.delete(CONVERSATION_ID);
            history.replace({ pathname, search: query.toString(), hash });
        }
    }, [hash, history, loadConversation, pathname, search]);

    return (
        <>
            <div className={`${BASE_CLASS_NAME}  ${offlineClassName}`}>
                <div className={`${BASE_CLASS_NAME}__header`}>
                    <Tooltip
                        disabled={isOnline}
                        content={<ChatOfflineTooltipContent />}
                        durationInMs={0}
                        hideOnClick={true}
                        placement={TooltipPlacement.AutoStart}>
                        <Button
                            cssClassName={`${BASE_CLASS_NAME}__button`}
                            style={ButtonStyles.Text}
                            onClick={handleClearChat}
                            aria-disabled={!isOnline}>
                            <Icon
                                cssClassName={`${BASE_CLASS_NAME}__icon`}
                                type={Icons.AutoRenew}
                            />
                            <Paragraph size={ParagraphSizes.XSmall}>
                                Clear
                            </Paragraph>
                        </Button>
                    </Tooltip>
                </div>

                <div
                    className={`${BASE_CLASS_NAME}__content`}
                    ref={containerRef}>
                    <ChatModalResults
                        containerRef={containerRef}
                        isBookview={true}
                    />

                    <ChatPrompt
                        newChatStarted={newChatStarted}
                        prompt={prompt}
                        handleSetPrompt={setPrompt}
                    />
                </div>
            </div>
            {!inMobileView && <BookviewTabPanelFooter />}
        </>
    );
};

// Wrap the content component with the provider so we can use the hook
const BookviewChatPanel: React.FC = () => (
    <CurrentConversationProvider atom={BookviewConversationAtom}>
        <BookviewChatPanelContent />
    </CurrentConversationProvider>
);

export default BookviewChatPanel;
